import { IForm } from 'core-components/classes/models/IForm'
import { HttpHeaders, HttpRequest } from 'core/classes/http'

export interface IDownloadedFile {
  fileName: string | null
  file: Blob | null
}

interface IDownloadFileArgs {
  method: 'get' | 'post'
  url: string
  token: string | null
  payload?: IForm
}

const FILE_NAME_PATTERN = /attachment; filename=(.+)/

function readContentDisposition(response: {
  headers?: unknown
  request?: { getResponseHeader?: (name: string) => string | null }
}): string | undefined {
  const headers = response.headers as
    | (Record<string, unknown> & { get?: (key: string) => unknown })
    | undefined

  const fromHeaders =
    typeof headers?.get === 'function'
      ? headers.get('Content-Disposition')
      : (headers?.['content-disposition'] ?? headers?.['Content-Disposition'])

  if (fromHeaders) {
    return String(fromHeaders)
  }

  return response.request?.getResponseHeader?.('Content-Disposition') ?? undefined
}

/**
 * The export endpoints answer with the file itself, so the transport's own error rebuild leaves
 * `data` as something other than a Blob — that is what tells a refused export from a delivered one.
 * The token is set here rather than left to the interceptor because these routes take it bare,
 * without the `Bearer` prefix the session routes expect — and with no token there is nothing to
 * ask with, so no request is made rather than one carrying an empty credential.
 */
export async function downloadFile({
  method,
  url,
  token,
  payload,
}: IDownloadFileArgs): Promise<IDownloadedFile> {
  if (!token) {
    return { fileName: null, file: null }
  }

  const headers = new HttpHeaders()
  headers.add('Authorization', token)

  const response = await new HttpRequest(headers)[method]({ url, payload })

  if (!(response?.data instanceof Blob)) {
    return { fileName: null, file: null }
  }

  const contentDisposition = readContentDisposition(response)
  const matched = contentDisposition?.match(FILE_NAME_PATTERN)

  return { fileName: matched?.[1] ?? null, file: response.data }
}
